'use client';
import { useEffect, useState } from 'react';
import { getMessaging, getToken } from 'firebase/messaging';
import firebaseApp from '@/lib/utils/firebase';
import { storage_keys } from '@/constant/configs';
import {
  isFCMSupported,
  getFCMUnsupportedReason,
  isInAppBrowser,
  getInAppBrowserType,
} from '@/lib/helpers/browserDetection';

const useFcmToken = () => {
  const [fcmToken, setFcmToken] = useState('');
  const [notificationPermissionStatus, setNotificationPermissionStatus] =
    useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSupported, setIsSupported] = useState(true);

  useEffect(() => {
    const retrieveToken = async () => {
      try {
        // Check if FCM is supported before doing anything
        if (!isFCMSupported()) {
          const reason = getFCMUnsupportedReason();
          console.warn('FCM not supported:', reason);
          setError(reason || 'FCM not supported');
          setIsSupported(false);

          // Log specific browser type for analytics
          const browserType = getInAppBrowserType();
          if (browserType) {
            // console.log(`Detected ${browserType} in-app browser`);
          }

          return;
        }

        // Step 1: Register service worker with error handling
        let registration;
        try {
          registration = await navigator.serviceWorker.register(
            '/firebase-messaging-sw.js',
            { scope: '/' }
          );

          // Wait for service worker to be ready
          await navigator.serviceWorker.ready;
          // console.log('Service Worker registered successfully');
        } catch (swError) {
          console.error('Service Worker registration failed:', swError);
          setError('Service Worker registration failed');
          return;
        }

        // Step 2: Check if Firebase is initialized
        if (!firebaseApp) {
          console.error('Firebase app not initialized');
          setError('Firebase not initialized');
          return;
        }

        // Step 2b: Get messaging instance with error handling
        let messaging;
        try {
          messaging = getMessaging(firebaseApp);
        } catch (messagingError: any) {
          console.error('Failed to get messaging instance:', messagingError);
          setError('Messaging service unavailable');
          return;
        }

        // Step 3: Request notification permission
        const permission = await Notification.requestPermission();
        setNotificationPermissionStatus(permission);

        if (permission === 'denied') {
          // console.log('Notification permission denied by user');
          setError('Notification permission denied');
          return;
        }

        if (permission !== 'granted') {
          // console.log('Notification permission not granted');
          return;
        }

        // Step 4: Get FCM token with error handling
        try {
          const vapidKey = process.env.NEXT_PUBLIC_FIREBASE_WEB_KEY;

          if (!vapidKey) {
            return;
          }

          const deviceToken = await getToken(messaging, {
            vapidKey,
            serviceWorkerRegistration: registration,
          });

          if (deviceToken) {
            setFcmToken(deviceToken);
            localStorage.setItem(
              storage_keys.firebase_device_token,
              deviceToken
            );
            setError(null);
            // console.log('FCM token retrieved successfully');
          } else {
            console.warn('No FCM token received');
            setError('No token received');
          }
        } catch (tokenError: any) {
          // Handle specific errors
          if (tokenError?.name === 'AbortError') {
            console.warn(
              'Push service unavailable. User may have blocked notifications or service is down.'
            );
            setError('Push service unavailable');
          } else {
            console.error('Error getting FCM token:', tokenError);
            setError('Failed to get FCM token');
          }
        }
      } catch (error: any) {
        console.error('Error in FCM setup:', error);
        setError(error?.message || 'Unknown error');
      }
    };

    // Only attempt FCM registration if supported
    retrieveToken();
  }, []);

  return {
    fcmToken,
    notificationPermissionStatus,
    firebaseApp,
    error,
    isSupported,
    isInAppBrowser: isInAppBrowser(),
  };
};

export default useFcmToken;
